import type { DrillFilter } from "../../types/status";
import { ensureUtc, pad2 } from "../../utils";

export function toDatetimeLocal(isoUtc: string): string {
  const d = new Date(ensureUtc(isoUtc));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

export function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return "—";
  return new Date(ensureUtc(dateStr)).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function incidentDuration(startedAt: string, resolvedAt?: string | null): string {
  if (!resolvedAt) return "ongoing";
  const secs = Math.max(0, Math.round((new Date(ensureUtc(resolvedAt)).getTime() - new Date(ensureUtc(startedAt)).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const CHANGELOG_FIELD_LABEL: Record<string, string> = {
  severity: "Severity",
  status: "Status",
  resolved_at: "Resolved at",
  root_cause: "Root cause",
  notes: "Notes",
  registrations_lost: "Registrations lost",
  ggr_impact: "GGR impact",
};

export function changelogChanges(meta: Record<string, unknown> | null | undefined): string[] {
  const changes = (meta as { changes?: Record<string, { old: unknown; new: unknown }> } | null)?.changes;
  if (!changes) return [];
  const show = (x: unknown) => (x === null || x === undefined || x === "" ? "—" : String(x));
  return Object.entries(changes).map(([field, v]) => `${CHANGELOG_FIELD_LABEL[field] ?? field}: ${show(v.old)} → ${show(v.new)}`);
}

const DRILL_KEYS = ["dfIds", "dfFrom", "dfTo", "dfSeverity", "dfType", "dfStatus", "dfLabel"];

export function clearDrillParams(params: URLSearchParams): void {
  DRILL_KEYS.forEach((key) => params.delete(key));
}

export function writeDrillFilter(params: URLSearchParams, filter: DrillFilter): void {
  clearDrillParams(params);
  if (filter.ids?.length) params.set("dfIds", filter.ids.join(","));
  if (filter.from) params.set("dfFrom", filter.from);
  if (filter.to) params.set("dfTo", filter.to);
  if (filter.severity) params.set("dfSeverity", filter.severity);
  if (filter.serviceType) params.set("dfType", filter.serviceType);
  if (filter.status) params.set("dfStatus", filter.status);
  params.set("dfLabel", filter.label);
}

export function parseDrillFilter(params: URLSearchParams): DrillFilter | null {
  const label = params.get("dfLabel");
  const ids = params.get("dfIds");
  const from = params.get("dfFrom");
  const to = params.get("dfTo");
  const severity = params.get("dfSeverity");
  const serviceType = params.get("dfType");
  const status = params.get("dfStatus");
  if (!label && !ids && !from && !to && !severity && !serviceType && !status) return null;
  return {
    label: label ?? "Filtered incidents",
    ids: ids ? ids.split(",") : undefined,
    from: from ?? undefined,
    to: to ?? undefined,
    severity: severity ?? undefined,
    serviceType: serviceType ?? undefined,
    status: status ?? undefined,
  };
}
